import { useEffect, useMemo, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { message, Popover } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import ToggleSwitch from "../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { formatWholeNumberInput } from "../../CommonComponents/FormattedInput/FormattedInput";
import { AppStrings } from "../../../utils/constants/AppStrings";
import useIsDevMode from "../../../hooks/useIsDevMode";

import "./stylesWelcomeBonusConfig.css";

const POPOVER_STYLES = {
  root: {
    maxWidth: 300,
    textAlign: "center",
    fontFamily: "Poppins-regular",
  },
};

const DEFAULT_STATE = {
  qualification: {
    minimum_required_purchase: "0",
  },
  sportsbook: {
    max_free_play_cash: "0",
    match_percentage: "0",
    minimum_odds: "1",
    minimum_number_of_parlay_legs: "1",
    expiry_days: "3",
    rollover: "1",
  },
  casino: {
    max_casino_cash: "0",
    match_percentage: "0",
  },
};

const QUERY_KEY = ["BONUS_CONFIGS", "WELCOME_BONUS_CONFIG"];

const isValidDecimal = (value) => value === "" || /^\d+(\.\d*)?$/.test(value);
const isValidInteger = (value) => value === "" || /^\d+$/.test(value);

function WelcomeBonusConfig() {
  const token = JSON.parse(localStorage.getItem("token"));
  const privateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
  const queryClient = useQueryClient();

  // Rollover is not implemented yet (BONENG-739). Keep it behind the
  // development-mode gate so it is hidden in released environments until ready.
  const isRolloverEnabled = useIsDevMode();

  const [config, setConfig] = useState(DEFAULT_STATE);
  const [initialConfig, setInitialConfig] = useState(DEFAULT_STATE);
  const [enabled, setEnabled] = useState(true);

  const { data: fetchedConfig, isLoading: isConfigLoading, isError: isConfigError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await privateAdminPanelInstance.get(API_ROUTES.FETCH_V2_WELCOME_BONUS_CONFIG);
      const payload = response?.data?.data ?? {};

      return {
        enabled: !(payload?.disabled ?? false),
        qualification: {
          minimum_required_purchase: String(payload?.qualification?.minimum_required_purchase ?? "0"),
        },
        sportsbook: {
          max_free_play_cash: String(payload?.sportsbook?.max_free_play_cash ?? "0"),
          match_percentage: String(payload?.sportsbook?.match_percentage ?? "0"),
          minimum_odds: String(payload?.sportsbook?.minimum_odds ?? "1"),
          minimum_number_of_parlay_legs: String(
            payload?.sportsbook?.minimum_number_of_parlay_legs ?? "1"
          ),
          expiry_days: String(payload?.sportsbook?.expiry_days ?? "3"),
          rollover: String(payload?.sportsbook?.rollover ?? "1"),
        },
        casino: {
          max_casino_cash: String(payload?.casino?.max_casino_cash ?? "0"),
          match_percentage: String(payload?.casino?.match_percentage ?? "0"),
        },
      };
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload) =>
      privateAdminPanelInstance.patch(API_ROUTES.UPDATE_V2_WELCOME_BONUS_CONFIG, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      message.success(AppStrings.VALUES_UPDATED_SUCCESSFULLY);
    },
    onError: () => {
      message.error(AppStrings.FAILED_TO_UPDATE_VALUES);
    },
  });

  useEffect(() => {
    if (!fetchedConfig) {
      return;
    }
    const { enabled: fetchedEnabled, ...nextConfig } = fetchedConfig;
    setConfig(nextConfig);
    setInitialConfig(nextConfig);
    setEnabled(fetchedEnabled);
  }, [fetchedConfig]);

  useEffect(() => {
    if (isConfigError) {
      message.error(AppStrings.FAILED_TO_FETCH_VALUES);
    }
  }, [isConfigError]);

  const isQualificationDirty = useMemo(
    () =>
      config.qualification.minimum_required_purchase !==
      initialConfig.qualification.minimum_required_purchase,
    [config.qualification, initialConfig.qualification]
  );
  const isSportsbookDirty = useMemo(
    () =>
      config.sportsbook.max_free_play_cash !== initialConfig.sportsbook.max_free_play_cash ||
      config.sportsbook.match_percentage !== initialConfig.sportsbook.match_percentage ||
      config.sportsbook.minimum_odds !== initialConfig.sportsbook.minimum_odds ||
      config.sportsbook.minimum_number_of_parlay_legs !==
        initialConfig.sportsbook.minimum_number_of_parlay_legs ||
      config.sportsbook.expiry_days !== initialConfig.sportsbook.expiry_days ||
      (isRolloverEnabled &&
        config.sportsbook.rollover !== initialConfig.sportsbook.rollover),
    [config.sportsbook, initialConfig.sportsbook, isRolloverEnabled]
  );
  const isCasinoDirty = useMemo(
    () =>
      config.casino.max_casino_cash !== initialConfig.casino.max_casino_cash ||
      config.casino.match_percentage !== initialConfig.casino.match_percentage,
    [config.casino, initialConfig.casino]
  );

  const updateSportsbookField = (key, value) => {
    if (key === "expiry_days" || key === "rollover") {
      setConfig((prev) => ({
        ...prev,
        sportsbook: {
          ...prev.sportsbook,
          [key]: formatWholeNumberInput(value),
        },
      }));
      return;
    }

    const isValid =
      key === "minimum_number_of_parlay_legs" ? isValidInteger(value) : isValidDecimal(value);
    if (!isValid) {
      return;
    }
    setConfig((prev) => ({
      ...prev,
      sportsbook: {
        ...prev.sportsbook,
        [key]: value,
      },
    }));
  };

  const updateQualificationField = (value) => {
    if (!isValidDecimal(value)) {
      return;
    }
    setConfig((prev) => ({
      ...prev,
      qualification: {
        ...prev.qualification,
        minimum_required_purchase: value,
      },
    }));
  };

  const updateCasinoField = (key, value) => {
    if (!isValidDecimal(value)) {
      return;
    }
    setConfig((prev) => ({
      ...prev,
      casino: {
        ...prev.casino,
        [key]: value,
      },
    }));
  };

  const handleResetSportsbook = () => {
    setConfig((prev) => ({ ...prev, sportsbook: { ...initialConfig.sportsbook } }));
  };

  const handleResetCasino = () => {
    setConfig((prev) => ({ ...prev, casino: { ...initialConfig.casino } }));
  };

  const handleResetQualification = () => {
    setConfig((prev) => ({ ...prev, qualification: { ...initialConfig.qualification } }));
  };

  const handleSaveQualification = () => {
    const minimumRequiredPurchase = parseFloat(config.qualification.minimum_required_purchase);
    if (Number.isNaN(minimumRequiredPurchase)) {
      message.error(AppStrings.FAILED_TO_UPDATE_VALUES);
      return;
    }

    updateMutation.mutate({
      qualification: {
        minimum_required_purchase: minimumRequiredPurchase,
      },
    });
  };

  const handleSaveSportsbook = () => {
    const maxFreePlayCash = parseFloat(config.sportsbook.max_free_play_cash);
    const matchPercentage = parseFloat(config.sportsbook.match_percentage);
    const minimumOdds = parseFloat(config.sportsbook.minimum_odds);
    const minimumNumberOfParlayLegs = parseInt(config.sportsbook.minimum_number_of_parlay_legs, 10);
    const expiryDays = parseInt(config.sportsbook.expiry_days, 10);
    const rollover = parseInt(config.sportsbook.rollover, 10);

    if (
      Number.isNaN(maxFreePlayCash) ||
      Number.isNaN(matchPercentage) ||
      Number.isNaN(minimumOdds) ||
      Number.isNaN(minimumNumberOfParlayLegs) ||
      Number.isNaN(expiryDays) ||
      (isRolloverEnabled && Number.isNaN(rollover))
    ) {
      message.error(AppStrings.FAILED_TO_UPDATE_VALUES);
      return;
    }

    updateMutation.mutate({
      sportsbook: {
        max_free_play_cash: maxFreePlayCash,
        match_percentage: matchPercentage,
        minimum_odds: minimumOdds,
        minimum_number_of_parlay_legs: minimumNumberOfParlayLegs,
        expiry_days: expiryDays,
        ...(isRolloverEnabled ? { rollover } : {}),
      },
    });
  };

  const handleSaveCasino = () => {
    const maxCasinoCash = parseFloat(config.casino.max_casino_cash);
    const matchPercentage = parseFloat(config.casino.match_percentage);
    if (Number.isNaN(maxCasinoCash) || Number.isNaN(matchPercentage)) {
      message.error(AppStrings.FAILED_TO_UPDATE_VALUES);
      return;
    }

    updateMutation.mutate({
      casino: {
        max_casino_cash: maxCasinoCash,
        match_percentage: matchPercentage,
      },
    });
  };

  const handleEnabledChange = (value) => {
    const nextEnabled = typeof value === "boolean" ? value : value.is_active;
    const previousEnabled = enabled;
    setEnabled(nextEnabled);
    updateMutation.mutate(
      {
        disabled: !nextEnabled,
      },
      {
        onError: () => setEnabled(previousEnabled),
      }
    );
  };

  const isApiRunning = isConfigLoading || updateMutation.isPending;

  return (
    <div className={`bonus-configs-layout ${isApiRunning ? "loading" : ""}`}>
      <div className="bonus-configs-columns">
        <div className="bonus-configs-column bonus-configs-column-left">
          <div className="bonus-configs-card">
            <h3>{AppStrings.WELCOME_BONUS}</h3>
            <div className="bonus-configs-toggle-row">
              <label>{AppStrings.WELCOME_BONUS_ENABLED}</label>
              <ToggleSwitch
                status={enabled}
                eventCode="welcome-bonus-enabled"
                updateEventCode={handleEnabledChange}
                onlyUpdate={false}
                disabled={isApiRunning}
              />
            </div>
          </div>

          <div className="bonus-configs-card">
            <h3>{AppStrings.SPORTSBOOK}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-sb-max-free-play-cash">
                {AppStrings.MAX_FREE_PLAY_CASH}
                <Popover content={AppStrings.WELCOME_MAX_FREE_PLAY_CASH_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-sb-max-free-play-cash"
                type="text"
                inputMode="decimal"
                value={config.sportsbook.max_free_play_cash}
                onChange={(e) => updateSportsbookField("max_free_play_cash", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-sb-match-percentage">
                {AppStrings.MATCH_PERCENTAGE}
                <Popover content={AppStrings.WELCOME_MATCH_PERCENTAGE_SPORTSBOOK_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-sb-match-percentage"
                type="text"
                inputMode="decimal"
                value={config.sportsbook.match_percentage}
                onChange={(e) => updateSportsbookField("match_percentage", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-sb-minimum-odds">
                {AppStrings.MINIMUM_ODDS}
                <Popover content={AppStrings.WELCOME_MIN_ODDS_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-sb-minimum-odds"
                type="text"
                inputMode="decimal"
                value={config.sportsbook.minimum_odds}
                onChange={(e) => updateSportsbookField("minimum_odds", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-sb-minimum-legs">
                {AppStrings.MINIMUM_PARLAY_LEGS}
                <Popover content={AppStrings.WELCOME_MIN_LEGS_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-sb-minimum-legs"
                type="text"
                inputMode="numeric"
                value={config.sportsbook.minimum_number_of_parlay_legs}
                onChange={(e) => updateSportsbookField("minimum_number_of_parlay_legs", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-sb-expiry-days">
                {AppStrings.EXPIRATION_PERIOD}
                <Popover content={AppStrings.WELCOME_EXPIRATION_PERIOD_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-sb-expiry-days"
                type="text"
                inputMode="numeric"
                value={config.sportsbook.expiry_days}
                onChange={(e) => updateSportsbookField("expiry_days", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            {isRolloverEnabled && (
              <div className="bonus-configs-field">
                <label htmlFor="welcome-sb-rollover">
                  {AppStrings.ROLLOVER}
                  <Popover content={AppStrings.WELCOME_ROLLOVER_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                    <InfoCircleOutlined className="info-icon" />
                  </Popover>
                </label>
                <input
                  id="welcome-sb-rollover"
                  type="text"
                  inputMode="numeric"
                  value={config.sportsbook.rollover}
                  onChange={(e) => updateSportsbookField("rollover", e.target.value)}
                  disabled={isApiRunning}
                />
              </div>
            )}
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetSportsbook} disabled={!isSportsbookDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveSportsbook} disabled={!isSportsbookDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>

          <div className="bonus-configs-card">
            <h3>{AppStrings.CASINO}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-casino-max-cash">
                {AppStrings.MAX_CASINO_CASH}
                <Popover content={AppStrings.WELCOME_MAX_CASINO_CASH_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-casino-max-cash"
                type="text"
                inputMode="decimal"
                value={config.casino.max_casino_cash}
                onChange={(e) => updateCasinoField("max_casino_cash", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-casino-match-percentage">
                {AppStrings.MATCH_PERCENTAGE}
                <Popover content={AppStrings.WELCOME_MATCH_PERCENTAGE_CASINO_TOOLTIP} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-casino-match-percentage"
                type="text"
                inputMode="decimal"
                value={config.casino.match_percentage}
                onChange={(e) => updateCasinoField("match_percentage", e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetCasino} disabled={!isCasinoDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveCasino} disabled={!isCasinoDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>
        </div>

        <div className="bonus-configs-column bonus-configs-column-right">
          <div className="bonus-configs-card">
            <h3>{AppStrings.QUALIFICATION}</h3>
            <div className="bonus-configs-field">
              <label htmlFor="welcome-min-required-purchase">
                {AppStrings.MINIMUM_REQUIRED_PURCHASE}
                <Popover content={AppStrings.MINIMUM_REQUIRED_PURCHASE_POPOVER_TEXT} trigger="hover" placement="topLeft" styles={POPOVER_STYLES}>
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </label>
              <input
                id="welcome-min-required-purchase"
                type="text"
                inputMode="decimal"
                value={config.qualification.minimum_required_purchase}
                onChange={(e) => updateQualificationField(e.target.value)}
                disabled={isApiRunning}
              />
            </div>
            <div className="bonus-configs-actions">
              <button type="button" onClick={handleResetQualification} disabled={!isQualificationDirty || isApiRunning}>
                {AppStrings.RESET}
              </button>
              <button type="button" onClick={handleSaveQualification} disabled={!isQualificationDirty || isApiRunning}>
                {isApiRunning ? <RebetLoader height="20px" width="20px" /> : AppStrings.SAVE_CHANGES}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeBonusConfig;
